import { CurrencyOption, DataType, FormatHint } from '../types';
import { DEFAULT_LOCALE } from './constants';

enum DenominatorCharacter {
  None = '',
  Thousands = 'k',
  Millions = 'm',
  Billions = 'b',
  Trillions = 't',
  Quadrillions = 'qa',
}

enum DenominatorValue {
  None = 1,
  Thousands = 1000,
  Millions = 1000000,
  Billions = 1000000000,
  Trillions = 1000000000000,
  Quadrillions = 1000000000000000,
}

export type Denominator = [number, string];

export class Formatting {
  private constructor() {}

  static format(
    value: number | string | Date | null,
    options: {
      dataType: DataType;
      formatHint?: FormatHint;
      isChange?: boolean;
      currency?: string;
      denominator?: Denominator;
      hyphenForNull?: boolean;
    }
  ): string | null {
    const { dataType, formatHint, isChange, currency = CurrencyOption.eur, denominator, hyphenForNull } = options;

    if (value === null) {
      return hyphenForNull ? '-' : null;
    }

    let formatted: string | null = null;

    if (formatHint) {
      switch (formatHint) {
        case FormatHint.Money:
          formatted = formatMoney(Number(value), currency, isChange, denominator);
          break;
        case FormatHint.WholeMoney:
          formatted = formatWholeMoney(Number(value), currency, isChange);
          break;
        case FormatHint.Percentage:
          formatted = formatPercentage(Number(value), isChange);
          break;
        case FormatHint.PercentagePoints:
          formatted = formatPercentagePoints(Number(value), isChange);
          break;
        case FormatHint.Index:
          formatted = formatIndexValue(Number(value), isChange);
          break;
        case FormatHint.MonthYear:
        case FormatHint.Month:
        case FormatHint.Year:
          formatted = formatDate(String(value), formatHint);
          break;
      }
    } else {
      switch (dataType) {
        case DataType.Integer:
          formatted = formatInteger(Number(value), isChange, denominator);
          break;
        case DataType.Decimal:
          formatted = formatDecimal(Number(value), isChange, denominator);
          break;
        case DataType.Date:
          formatted = formatDate(String(value));
          break;
        default:
          formatted = String(value);
          break;
      }
    }

    if (formatted === null) {
      return hyphenForNull ? '-' : null;
    }

    return formatted;
  }

  static getCurrencySymbol(code: string): string {
    return getCurrencySymbol(code);
  }
}

function getCurrencySymbol(code: string): string {
  const currencySign =
    new Intl.NumberFormat(DEFAULT_LOCALE, { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' })
      .formatToParts(0)
      .find(part => part.type === 'currency')?.value || '';
  return currencySign || '';
}

function getDenominator(value: number): Denominator {
  const absValue = Math.abs(value);

  if (absValue === Infinity) {
    return [DenominatorValue.None, DenominatorCharacter.None];
  }

  if (absValue >= DenominatorValue.Quadrillions) {
    return [DenominatorValue.Quadrillions, DenominatorCharacter.Quadrillions];
  }

  if (absValue >= DenominatorValue.Trillions) {
    return [DenominatorValue.Trillions, DenominatorCharacter.Trillions];
  }

  if (absValue >= DenominatorValue.Billions) {
    return [DenominatorValue.Billions, DenominatorCharacter.Billions];
  }

  if (absValue >= DenominatorValue.Millions) {
    return [DenominatorValue.Millions, DenominatorCharacter.Millions];
  }

  if (absValue >= 10000) {
    return [DenominatorValue.Thousands, DenominatorCharacter.Thousands];
  }

  return [DenominatorValue.None, DenominatorCharacter.None];
}

function roundToDigits(value: number, fractionDigits: number = 2) {
  const factor = Math.pow(10, fractionDigits);
  return Math.round(value * factor) / factor;
}

function formatNumber(
  value: number,
  options: {
    getFractionDigits: (denom: DenominatorValue) => number;
    denominator?: Denominator;
    isChange?: boolean;
    currency?: string;
    valuePrefix?: string;
    suffix?: string;
  }
): string {
  const { getFractionDigits, denominator, isChange = false, currency, valuePrefix = '', suffix = '' } = options;
  const denoms = denominator || getDenominator(value);
  const fractionDigits = getFractionDigits(denoms[0]);
  const shrunken = Math.abs(roundToDigits(value / denoms[0], fractionDigits));

  const valueString = shrunken.toLocaleString(DEFAULT_LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    ...(currency ? { style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol' } : {}),
  });

  const isNegative = value < 0;
  const signPrefix = isNegative ? '-' : isChange ? '+' : '';
  return `${signPrefix}${valuePrefix}${valueString}${denominator ? '' : denoms[1]}${suffix}`;
}

function formatMoney(value: number, currency: string, isChange: boolean = false, denominator?: Denominator): string {
  return formatNumber(value, {
    getFractionDigits: denom => (denom === DenominatorValue.None ? 2 : 1),
    denominator,
    isChange,
    currency,
  });
}
function formatWholeMoney(value: number, currency: string, isChange: boolean = false): string {
  return formatNumber(value, {
    getFractionDigits: denom => (denom === DenominatorValue.None && Number.isInteger(value) ? 0 : 2),
    isChange,
    currency,
  });
}

function formatInteger(value: number, isChange: boolean = false, denominator?: Denominator): string {
  return formatNumber(value, {
    getFractionDigits: denom => (denom === DenominatorValue.None ? 0 : 1),
    denominator,
    isChange,
  });
}

function formatDecimal(value: number, isChange: boolean = false, denominator?: Denominator): string {
  return formatNumber(value, {
    getFractionDigits: denom => (denom === DenominatorValue.None ? 2 : 1),
    denominator,
    isChange,
  });
}

function formatPercentage(value: number, isChange: boolean = false): string {
  let percentValue = value * 100;
  let valuePrefix = '';
  if (isChange && Math.abs(percentValue) > 999) {
    percentValue = percentValue < 0 ? -999 : 999;
    valuePrefix = '>';
  }
  return formatNumber(percentValue, {
    getFractionDigits: () => 1,
    denominator: [DenominatorValue.None, DenominatorCharacter.None],
    isChange,
    valuePrefix,
    suffix: '%',
  });
}

function formatPercentagePoints(value: number, isChange: boolean = false): string {
  const pointsValue = value * 100;
  return formatNumber(pointsValue, {
    getFractionDigits: () => 1,
    denominator: [DenominatorValue.None, DenominatorCharacter.None],
    isChange,
    suffix: 'pp',
  });
}

function formatIndexValue(value: number, isChange: boolean): string {
  return formatNumber(value, {
    getFractionDigits: () => 0,
    denominator: [DenominatorValue.None, DenominatorCharacter.None],
    isChange,
  });
}

function formatDate(value: string, formatHint?: FormatHint): string | null {
  const date = new Date(value);

  if (date instanceof Date && isNaN(date.getTime())) {
    return null;
  }

  if (formatHint === FormatHint.MonthYear) {
    return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  if (formatHint === FormatHint.Month) {
    return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
      month: 'short',
    }).format(date);
  }

  if (formatHint === FormatHint.Year) {
    return Intl.DateTimeFormat(DEFAULT_LOCALE, {
      year: 'numeric',
    }).format(date);
  }

  return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    dateStyle: 'long',
  }).format(date);
}
